/**
 * Batch #14.14 — Portail Client : Mode d'emploi (guides utilisateur).
 *
 * Affiche une grille de mini-tuiles (1 par module portail). Click sur une
 * mini-tuile → vue détail qui embarque le PDF de guide correspondant
 * (servi statiquement depuis `/guides/{slug}.pdf`).
 *
 * Si le PDF n'est pas (encore) déposé → coquille "Bientôt disponible".
 *
 * Pour ajouter un guide : déposer le PDF dans `/app/frontend/public/guides/{slug}.pdf`.
 */
import { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft, Download, ExternalLink, Loader2,
  Home, FileText, FileSignature, Receipt, MessageCircle,
  Calendar, Upload, Briefcase, BriefcaseBusiness, User, BookOpen, HandCoins,
} from "lucide-react";
import ImmeubleGuide from "@/pages/portal/guides/ImmeubleGuide";
import GenericWebGuide from "@/pages/portal/guides/GenericWebGuide";
import {
  documentsGuide, envoyerGuide, chatGuide, rdvGuide,
  mandatsGuide, facturesGuide, t777Guide, infoGuide,
} from "@/pages/portal/guides/configs";
import { paieGuide, entrepriseGuide } from "@/pages/portal/guides/configsPaie";
import { TONE_BG } from "@/constants/tones";

// Batch #14.37 — Map des configs guides web. Si un slug est listé ici,
// il utilise le nouveau format web (pas le PDF iframe).
// Batch #14.252 — + Paie et Entreprise.
const WEB_GUIDE_CONFIGS = {
  documents:  documentsGuide,
  envoyer:    envoyerGuide,
  chat:       chatGuide,
  rdv:        rdvGuide,
  mandats:    mandatsGuide,
  factures:   facturesGuide,
  t777:       t777Guide,
  info:       infoGuide,
  paie:       paieGuide,
  travail_autonome: entrepriseGuide,
};

const GUIDES = [
  {
    slug: "immeuble",
    label: "Immeuble",
    desc: "Logements, baux, maintenance, paiements, enquête de crédit",
    icon: Home,
    tone: "cyan",
  },
  {
    slug: "documents",
    label: "Documents reçus",
    desc: "Consultation et téléchargement des documents du cabinet",
    icon: FileText,
    tone: "blue",
  },
  {
    slug: "envoyer",
    label: "Envoyer un fichier",
    desc: "Téléverser vos documents vers le cabinet",
    icon: Upload,
    tone: "emerald",
  },
  {
    slug: "chat",
    label: "Chat & questions",
    desc: "Communiquer avec votre conseiller",
    icon: MessageCircle,
    tone: "indigo",
  },
  {
    slug: "rdv",
    label: "Rendez-vous",
    desc: "Prendre et gérer vos rendez-vous",
    icon: Calendar,
    tone: "violet",
  },
  {
    slug: "mandats",
    label: "Mandats à signer",
    desc: "Signer électroniquement vos mandats",
    icon: FileSignature,
    tone: "rose",
  },
  {
    slug: "factures",
    label: "Factures cabinet",
    desc: "Consulter et payer vos factures de services",
    icon: Receipt,
    tone: "amber",
  },
  {
    slug: "t777",
    label: "Bureau à domicile",
    desc: "Déclaration des dépenses T777 (télétravail)",
    icon: Briefcase,
    tone: "amber",
  },
  {
    slug: "travail_autonome",
    label: "Entreprise",
    desc: "Dépenses avec reçus photo, kilométrage et factures de votre entreprise",
    icon: BriefcaseBusiness,
    tone: "emerald",
  },
  {
    slug: "paie",
    label: "Paie",
    desc: "Fiches employés, feuilles de temps, documents de paie reçus et remises DAS",
    icon: HandCoins,
    tone: "amber",
  },
  {
    slug: "info",
    label: "Mon profil",
    desc: "Mes informations personnelles et mes accès",
    icon: User,
    tone: "cyan",
  },
];


function GuideCard({ guide, onOpen }) {
  const Icon = guide.icon;
  return (
    <button
      onClick={() => onOpen(guide)}
      className={`group bg-gradient-to-br ${TONE_BG[guide.tone]} rounded-2xl p-5 flex flex-col items-start text-left transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-lg border min-h-[160px]`}
      data-testid={`mode-emploi-card-${guide.slug}`}
    >
      <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-3 group-hover:shadow-md transition">
        <Icon className="w-6 h-6" />
      </div>
      <div className="font-semibold text-base mb-1">{guide.label}</div>
      <div className="text-xs opacity-75 leading-snug">{guide.desc}</div>
    </button>
  );
}

function GuideViewer({ guide, onBack, primaryColor }) {
  const [status, setStatus] = useState("checking"); // "checking" | "available" | "missing"
  const pdfUrl = `${process.env.PUBLIC_URL || ""}/guides/${guide.slug}.pdf`;

  useEffect(() => {
    let cancelled = false;
    setStatus("checking");
    // HEAD-request pour savoir si le fichier existe avant d'afficher l'iframe
    fetch(pdfUrl, { method: "HEAD" })
      .then((r) => {
        if (cancelled) return;
        if (r.ok && (r.headers.get("content-type") || "").includes("pdf")) {
          setStatus("available");
        } else {
          setStatus("missing");
        }
      })
      .catch(() => { if (!cancelled) setStatus("missing"); });
    return () => { cancelled = true; };
  }, [pdfUrl]);

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = `Guide-${guide.label.replace(/[^\w]/g, "-")}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleOpenNewTab = () => {
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-4" data-testid={`mode-emploi-viewer-${guide.slug}`}>
      {/* Header avec retour + titre + actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition"
            data-testid="mode-emploi-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Retour aux guides
          </button>
        </div>
        {status === "available" && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleOpenNewTab}
              className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 inline-flex items-center gap-1.5"
              data-testid="mode-emploi-open-newtab-btn"
            >
              <ExternalLink className="w-4 h-4" /> Ouvrir dans un nouvel onglet
            </button>
            <button
              onClick={handleDownload}
              className="px-3 py-1.5 rounded-lg text-white text-sm font-medium hover:opacity-90 inline-flex items-center gap-1.5"
              style={{ background: primaryColor }}
              data-testid="mode-emploi-download-btn"
            >
              <Download className="w-4 h-4" /> Télécharger en PDF
            </button>
          </div>
        )}
      </div>

      {/* Titre du module */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-3 mb-1">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${primaryColor}15` }}
          >
            <guide.icon className="w-5 h-5" style={{ color: primaryColor }} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Guide : {guide.label}</h3>
            <p className="text-xs text-gray-500">{guide.desc}</p>
          </div>
        </div>
      </div>

      {/* Visionneuse PDF */}
      {status === "checking" && (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center" data-testid="mode-emploi-loading">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-3" />
          <p className="text-sm text-slate-500">Chargement du guide…</p>
        </div>
      )}

      {status === "missing" && (
        <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center" data-testid="mode-emploi-missing">
          <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h4 className="text-base font-semibold text-slate-700 mb-1">Guide bientôt disponible</h4>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Le mode d'emploi de ce module est en cours de rédaction par notre équipe.
            Il sera publié ici prochainement. Merci de votre patience.
          </p>
          <button
            onClick={onBack}
            className="mt-5 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
            data-testid="mode-emploi-back-missing-btn"
          >
            Voir les autres guides
          </button>
        </div>
      )}

      {status === "available" && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="mode-emploi-pdf-frame-wrap">
          <iframe
            src={`${pdfUrl}#toolbar=1&navpanes=0`}
            title={`Guide ${guide.label}`}
            className="w-full"
            style={{ height: "75vh", minHeight: "600px", border: 0 }}
            data-testid="mode-emploi-pdf-iframe"
          />
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500 text-center">
            Si l'aperçu ne s'affiche pas, utilisez les boutons <strong>Télécharger</strong> ou <strong>Ouvrir dans un nouvel onglet</strong> ci-dessus.
          </div>
        </div>
      )}
    </div>
  );
}

export default function ModeEmploiTab({ primaryColor = "#072354", initialSlug = null }) {
  const [selected, setSelected] = useState(null);

  const openGuide = useCallback((guide) => {
    setSelected(guide);
    // Scroll en haut quand on ouvre un guide
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  }, []);

  const closeGuide = useCallback(() => {
    setSelected(null);
  }, []);

  // Batch #14.173 — Aide contextuelle : si un slug initial est fourni
  // (déclenché depuis le bouton ❓ d'une tuile), pré-sélectionne le guide.
  useEffect(() => {
    if (!initialSlug) return;
    const target = GUIDES.find((g) => g.slug === initialSlug);
    if (target) {
      setSelected(target);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    }
  }, [initialSlug]);

  if (selected) {
    // Batch #14.34 — Immeuble = page web dédiée (illustrations SVG riches)
    if (selected.slug === "immeuble") {
      return <ImmeubleGuide onBack={closeGuide} primaryColor={primaryColor} />;
    }
    // Batch #14.37 / #14.252 — les autres guides utilisent le composant générique
    const webConfig = WEB_GUIDE_CONFIGS[selected.slug];
    if (webConfig) {
      return <GenericWebGuide config={webConfig} onBack={closeGuide} />;
    }
    // Repli : PDF déposé dans /public/guides/{slug}.pdf
    return <GuideViewer guide={selected} onBack={closeGuide} primaryColor={primaryColor} />;
  }

  return (
    <div className="space-y-5" data-testid="mode-emploi-tab">
      <div className="bg-gradient-to-r from-violet-50 to-blue-50 rounded-2xl border border-violet-100 p-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-violet-700" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Bienvenue dans le mode d'emploi</h3>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">
          Choisissez un module ci-dessous pour consulter son guide d'utilisation détaillé,
          accompagné des captures d'écran correspondantes. Vous pourrez télécharger
          chaque guide au format PDF pour le consulter hors ligne.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mode-emploi-grid">
        {GUIDES.map((g) => (
          <GuideCard key={g.slug} guide={g} onOpen={openGuide} />
        ))}
      </div>
    </div>
  );
}
